/**
 * useStaticData Hook
 *
 * Provides access to pre-fetched static data that was baked into the build.
 * Falls back to default values if static data is not available.
 */
import { CDN_URL } from '@/config/cdn';

// Try to import static data - this file is generated at build time
let staticData = null;
try {
  staticData = require('../data/staticData.json');
} catch {
  console.warn('Static data not found, using defaults. Run `yarn fetch-data` to generate.');
}

// Default values (fallbacks if static data is not available)
// Phone numbers:
//   phone                = Owaza office line (primary public contact)
//   direct_line_phone    = Hanshi Jim Rivera's personal/direct line
const defaults = {
  businessInfo: {
    name: "Owaza Ryu Ju-Jitsu",
    phone: "(352) 428-0086",
    direct_line_phone: "(352) 428-0086",
    direct_line_label: "Hanshi Jim Rivera's direct line",
    email: "info@owazamartialarts.com",
    quickstart_fee: "$60",
  },
  heroContent: {
    title_line1: "Master Your",
    title_line2: "Inner Warrior",
    subtitle: "Expert martial arts training in Spring Hill, FL. Certified Self-Defense • Kids to Adults • Results-driven approach",
    media_type: "image",
    hero_image_url: `${CDN_URL}/images/hero.png`,
    hero_video_url: null,
    video_thumbnail_url: null,
  },
  programs: [],
  testimonials: [],
  schedule: {},
  trustIndicators: [
    { text: "As seen on News 8, 9 & 10" },
    { text: "Certified Self-Defense" },
    { text: "★★★★★ Google-Rated" }
  ],
  navigationLinks: [],
};

/**
 * Get static data for a specific key
 * @param {string} key - The data key (e.g., 'businessInfo', 'heroContent')
 * @returns {any} The static data or default value
 */
export function getStaticData(key) {
  if (staticData && staticData[key] !== null && staticData[key] !== undefined) {
    return staticData[key];
  }
  return defaults[key] || null;
}

/**
 * Get all static data
 * @returns {object} All static data with defaults applied
 */
export function getAllStaticData() {
  return {
    ...defaults,
    ...staticData,
  };
}

/**
 * Check if static data was loaded
 * @returns {boolean}
 */
export function hasStaticData() {
  return staticData !== null;
}

/**
 * Get the timestamp when static data was fetched
 * @returns {string|null}
 */
export function getStaticDataTimestamp() {
  return staticData?.fetchedAt || null;
}

export default {
  getStaticData,
  getAllStaticData,
  hasStaticData,
  getStaticDataTimestamp,
};
